/**
 * Helper functions for working with localStorage to store and retrieve duel room data
 */

/**
 * Save a room to localStorage
 * @param {Object} room - Room object to save
 */
export const saveRoomToLocalStorage = (room) => {
  try {
    // Get existing rooms
    const rooms = JSON.parse(localStorage.getItem('duelRooms') || '[]');
    
    // Check if room already exists
    const existingRoomIndex = rooms.findIndex(r => r.roomId === room.roomId);
    
    if (existingRoomIndex !== -1) {
      // Update existing room
      rooms[existingRoomIndex] = room;
    } else {
      // Add new room
      rooms.push(room);
    }
    
    // Save back to localStorage
    localStorage.setItem('duelRooms', JSON.stringify(rooms));
    
    console.log('Room saved to localStorage:', room.roomId);
    return true;
  } catch (error) {
    console.error('Error saving room to localStorage:', error);
    return false;
  }
};

/**
 * Get a room from localStorage by ID
 * @param {string} roomId - Room ID to retrieve
 * @returns {Object|null} Room object or null if not found
 */
export const getRoomFromLocalStorage = (roomId) => {
  try {
    const rooms = JSON.parse(localStorage.getItem('duelRooms') || '[]');
    return rooms.find(room => room.roomId === roomId) || null;
  } catch (error) {
    console.error('Error getting room from localStorage:', error);
    return null;
  }
};

/**
 * Get all rooms from localStorage
 * @returns {Array} Array of room objects
 */
export const getAllRoomsFromLocalStorage = () => {
  try {
    return JSON.parse(localStorage.getItem('duelRooms') || '[]');
  } catch (error) {
    console.error('Error getting all rooms from localStorage:', error);
    return [];
  }
};

/**
 * Remove a room from localStorage
 * @param {string} roomId - Room ID to remove
 * @returns {boolean} Success status
 */
export const removeRoomFromLocalStorage = (roomId) => {
  try {
    const rooms = JSON.parse(localStorage.getItem('duelRooms') || '[]');
    const newRooms = rooms.filter(room => room.roomId !== roomId);
    localStorage.setItem('duelRooms', JSON.stringify(newRooms));
    return true;
  } catch (error) {
    console.error('Error removing room from localStorage:', error);
    return false;
  }
};

/**
 * Update a room in localStorage
 * @param {string} roomId - Room ID to update
 * @param {Object} updatedData - Updated room data 
 * @returns {Object|null} Updated room object or null if not found
 */
export const updateRoomInLocalStorage = (roomId, updatedData) => {
  try {
    const rooms = JSON.parse(localStorage.getItem('duelRooms') || '[]');
    const roomIndex = rooms.findIndex(room => room.roomId === roomId);
    
    if (roomIndex === -1) {
      return null;
    }
    
    // Create updated room by merging existing with updates
    const updatedRoom = {
      ...rooms[roomIndex],
      ...updatedData
    };
    
    // Special handling for nested properties like participants and chat
    if (updatedData.participants) {
      updatedRoom.participants = updatedData.participants;
    }
    
    if (updatedData.chat) {
      updatedRoom.chat = updatedData.chat;
    }
    
    rooms[roomIndex] = updatedRoom;
    localStorage.setItem('duelRooms', JSON.stringify(rooms));
    return updatedRoom;
  } catch (error) {
    console.error('Error updating room in localStorage:', error);
    return null;
  }
};

/**
 * Add a chat message to a room in localStorage
 * @param {string} roomId - Room ID 
 * @param {Object} message - Chat message to add
 * @returns {boolean} Success status
 */
export const addChatMessageToRoom = (roomId, message) => {
  try {
    const rooms = JSON.parse(localStorage.getItem('duelRooms') || '[]');
    const roomIndex = rooms.findIndex(room => room.roomId === roomId);
    
    if (roomIndex === -1) {
      return false;
    }
    
    // Add message to chat
    if (!rooms[roomIndex].chat) {
      rooms[roomIndex].chat = [];
    }
    
    rooms[roomIndex].chat.push(message);
    
    localStorage.setItem('duelRooms', JSON.stringify(rooms));
    return true;
  } catch (error) {
    console.error('Error adding chat message to room:', error);
    return false;
  }
};

/**
 * Update participant ready status in a room
 * @param {string} roomId - Room ID
 * @param {string} userId - User ID
 * @param {boolean} isReady - Ready status
 * @returns {boolean} Success status
 */
export const updateParticipantReadyStatus = (roomId, userId, isReady) => {
  try {
    const rooms = JSON.parse(localStorage.getItem('duelRooms') || '[]');
    const roomIndex = rooms.findIndex(room => room.roomId === roomId);
    
    if (roomIndex === -1) {
      return false;
    }
    
    const participantIndex = rooms[roomIndex].participants.findIndex(
      p => p.userId === userId
    );
    
    if (participantIndex === -1) {
      return false;
    }
    
    rooms[roomIndex].participants[participantIndex].isReady = isReady;
    
    localStorage.setItem('duelRooms', JSON.stringify(rooms));
    return true;
  } catch (error) {
    console.error('Error updating participant ready status:', error);
    return false;
  }
};
